import React, { useEffect, useRef, ComponentType } from 'react';
import { analytics, EventProperties } from './posthogClient';
export interface TrackingEventConfig<P> {
  event: string;
  mapPropsToProperties?: (props: P) => EventProperties;
  properties?: EventProperties;
}
export interface TrackingConfig<P> {

  callbacks?: Record<string, TrackingEventConfig<P>>;

  lifecycle?: {
    onMount?: TrackingEventConfig<P>;
    onUnmount?: TrackingEventConfig<P>;
  };
}

export interface InjectedTrackingProps {
  trackEvent?: string;
  trackProperties?: EventProperties;
}
export function withTracking<P extends Record<string, any>>(
  WrappedComponent: ComponentType<P>,
  config: TrackingConfig<P> = {},
) {
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  const TrackedComponent = React.forwardRef<
    any,
    P & InjectedTrackingProps
  >((props, ref) => {
    const mountTimeRef = useRef<number>(Date.now());
    const propsRef = useRef<P>(props as unknown as P);
    propsRef.current = props as unknown as P;
    useEffect(() => {
      mountTimeRef.current = Date.now();
      if (config.lifecycle?.onMount) {
        const { event, mapPropsToProperties, properties } =
          config.lifecycle.onMount;
        analytics.capture(event, {
          componentName: displayName,
          ...properties,
          ...(mapPropsToProperties?.(propsRef.current) ?? {}),
        });
      }
      return () => {
        if (config.lifecycle?.onUnmount) {
          const { event, mapPropsToProperties, properties } =
            config.lifecycle.onUnmount;
          const duration = Date.now() - mountTimeRef.current;
          analytics.capture(event, {
            componentName: displayName,
            duration,
            ...properties,
            ...(mapPropsToProperties?.(propsRef.current) ?? {}),
          });
        }
      };
    }, []);
    const { trackEvent, trackProperties, ...restProps } = props;
    const interceptedProps: Record<string, any> = { ...restProps };
    if (config.callbacks) {
      for (const [callbackName, trackingCfg] of Object.entries(
        config.callbacks,
      )) {
        const originalCallback = (props as Record<string, any>)[
          callbackName
        ];
        interceptedProps[callbackName] = (...args: any[]) => {
          const eventName = trackEvent || trackingCfg.event;
          analytics.capture(eventName, {
            componentName: displayName,
            ...trackingCfg.properties,
            ...(trackingCfg.mapPropsToProperties?.(
              propsRef.current,
            ) ?? {}),
            ...trackProperties,
          });
          if (typeof originalCallback === 'function') {
            return originalCallback(...args);
          }
        };
      }
    }
    return <WrappedComponent ref={ref} {...(interceptedProps as P)} />;
  });
  TrackedComponent.displayName = `withTracking(${displayName})`;
  return TrackedComponent;
}
